<template>
	<div class="analytics-list" :class="{ 'is-embedded': !showHeader }">
		<header v-if="showHeader" class="list-header">
			<h2 class="list-title">Analytics reports</h2>
		</header>

		<p v-if="showLoading" class="payment-list-message" role="status">Loading analytics reports…</p>
		<div v-else-if="error" class="analytics-error">
			<p class="payment-list-message payment-error" role="alert">{{ error }}</p>
			<button class="payment-list-button" type="button" @click="loadReports()">Try again</button>
		</div>
		<template v-else>
			<section class="charts-grid" aria-label="Report charts">
				<AnalyticsChartCard
					title="Revenue and cost breakdown"
					subtitle="Cleaner pay + purchases + gross margin compared with revenue"
					:option="revenueCostBreakdownOption"
					:empty="!hasChartData"
					empty-text="Create reports to see financial trend charts."
				/>
				<AnalyticsChartCard
					title="Gross margin trend"
					subtitle="Gross margin amount and margin percent by period"
					:option="grossMarginTrendOption"
					:empty="!hasChartData"
					empty-text="Create reports to see margin trend."
				/>
			</section>

			<div class="payment-list-toolbar">
				<button class="payment-list-button" type="button" @click="handleCreate">
					<AppointmentIcon name="plus" />
					New report
				</button>
			</div>

			<div class="payment-list-content">
				<div class="payment-list-scroll">
					<table class="payment-list-table analytics-reports-table" aria-label="Analytics reports">
						<thead>
							<tr>
								<th>Period</th>
								<th class="numeric">Revenue (ex VAT)</th>
								<th class="numeric">Cleaner pay</th>
								<th class="numeric">Purchases</th>
								<th class="numeric">Gross margin</th>
								<th class="actions-col" aria-label="Actions"></th>
							</tr>
						</thead>
						<tbody>
							<tr v-if="!reports.length">
								<td colspan="6" class="payment-empty">No analytics reports yet.</td>
							</tr>
							<tr
								v-for="report in reports"
								:key="report.id"
								class="payment-list-row"
								@click="openReport(report)"
							>
								<td>{{ formatPeriod(report) }}</td>
								<td class="numeric amount">{{ formatCurrency(report.summary?.revenue) }}</td>
								<td class="numeric amount">{{ formatCurrency(report.summary?.laborCost) }}</td>
								<td class="numeric amount">{{ formatCurrency(report.summary?.purchasesGross) }}</td>
								<td class="numeric amount" :class="{ negative: (report.summary?.grossMargin || 0) < 0 }">
									{{ formatCurrency(report.summary?.grossMargin) }}
								</td>
								<td class="actions-col" @click.stop>
									<RowHoldMenu @confirm="handleDelete(report)">Hold to delete report</RowHoldMenu>
								</td>
							</tr>
						</tbody>
					</table>
				</div>
				<PaymentPagination
					v-if="pagination.pageCount > 1 || pagination.total"
					:page="pagination.page || 1"
					:page-count="pagination.pageCount || 1"
					:page-size="pagination.pageSize || 25"
					:total="pagination.total || 0"
					:loading="isLoading"
					@change="changePage"
				/>
			</div>
		</template>
	</div>
</template>

<script setup>
import { computed, getCurrentInstance, onMounted, ref } from 'vue';
import { format } from 'date-fns';
import { useAnalyticsReportsStore } from '@/stores/analyticsReports';
import { sameRecords, useLiveRefresh } from '@/composables/useLiveRefresh';
import AnalyticsChartCard from '@/components/analytics/AnalyticsChartCard.vue';
import AppointmentIcon from '@/components/icons/AppointmentIcon.vue';
import PaymentPagination from '@/components/payments/PaymentPagination.vue';
import RowHoldMenu from '@/components/common/RowHoldMenu.vue';
import '@/assets/styles/payment-lists.css';

defineProps({
	showHeader: {
		type: Boolean,
		default: true,
	},
});

const emit = defineEmits(['open-editor']);

const store = useAnalyticsReportsStore();
const { proxy } = getCurrentInstance();
const logError = proxy?.$logError;

const currentPage = ref(1);

const isLoading = computed(() => store.isLoading);
const error = computed(() => store.error);
const reports = computed(() => store.reports);
const chartReportsSource = computed(() =>
	store.chartReports.length ? store.chartReports : store.reports,
);
const pagination = computed(() => store.pagination);
const showLoading = computed(() => isLoading.value && !reports.value.length);

const toNumber = (value, fallback = 0) => {
	const numeric = Number(value);
	return Number.isFinite(numeric) ? numeric : fallback;
};

const parseDate = (value) => {
	if (!value) return null;
	const date = new Date(value);
	return Number.isNaN(date.getTime()) ? null : date;
};

const formatCompactCurrency = (value) =>
	new Intl.NumberFormat('en-GB', {
		style: 'currency',
		currency: 'GBP',
		notation: 'compact',
		maximumFractionDigits: 1,
	}).format(toNumber(value, 0));

const formatChartPeriod = (report) => {
	const fromDate = parseDate(report?.dateFrom);
	const toDate = parseDate(report?.dateTo);
	if (fromDate && toDate) {
		const sameMonth =
			fromDate.getMonth() === toDate.getMonth() &&
			fromDate.getFullYear() === toDate.getFullYear();
		return sameMonth
			? format(fromDate, 'MMM yyyy')
			: `${format(fromDate, 'dd MMM')} - ${format(toDate, 'dd MMM')}`;
	}
	const referenceDate = toDate || fromDate || parseDate(report?.updatedAt);
	return referenceDate ? format(referenceDate, 'MMM yyyy') : '—';
};

const chartReports = computed(() => {
	const sorted = [...chartReportsSource.value].sort((a, b) => {
		const aDate = parseDate(a?.dateTo) || parseDate(a?.dateFrom) || parseDate(a?.updatedAt);
		const bDate = parseDate(b?.dateTo) || parseDate(b?.dateFrom) || parseDate(b?.updatedAt);
		return (aDate?.getTime() || 0) - (bDate?.getTime() || 0);
	});
	if (sorted.length <= 18) return sorted;
	return sorted.slice(sorted.length - 18);
});

const chartPeriods = computed(() => chartReports.value.map((report) => formatChartPeriod(report)));
const hasChartData = computed(() => chartReports.value.length > 0);

const revenueCostBreakdownOption = computed(() => {
	const rows = chartReports.value;
	return {
		color: ['#2F6FED', '#9AA6B2', '#27A376', '#1C4E80'],
		tooltip: { trigger: 'axis' },
		legend: {
			top: 0,
			data: ['Cleaner pay', 'Purchases', 'Gross margin', 'Revenue'],
			textStyle: { color: '#5e6170', fontSize: 11 },
		},
		grid: { left: 12, right: 16, top: 44, bottom: 24, containLabel: true },
		xAxis: {
			type: 'category',
			data: chartPeriods.value,
			axisLabel: {
				rotate: chartPeriods.value.length > 8 ? 25 : 0,
				color: 'var(--text-color-secondary, #5e6170)',
				fontSize: 11,
			},
			axisLine: { lineStyle: { color: 'var(--border-color, #dcdcdc)' } },
		},
		yAxis: {
			type: 'value',
			axisLabel: {
				color: 'var(--text-color-secondary, #5e6170)',
				fontSize: 11,
				formatter: (value) => formatCompactCurrency(value),
			},
			splitLine: { lineStyle: { color: 'rgba(0, 0, 0, 0.08)' } },
		},
		series: [
			{
				name: 'Cleaner pay',
				type: 'bar',
				stack: 'pnl',
				data: rows.map((report) => toNumber(report?.summary?.laborCost)),
				barMaxWidth: 28,
			},
			{
				name: 'Purchases',
				type: 'bar',
				stack: 'pnl',
				data: rows.map((report) => toNumber(report?.summary?.purchasesGross)),
				barMaxWidth: 28,
			},
			{
				name: 'Gross margin',
				type: 'bar',
				stack: 'pnl',
				data: rows.map((report) => toNumber(report?.summary?.grossMargin)),
				barMaxWidth: 28,
			},
			{
				name: 'Revenue',
				type: 'line',
				smooth: true,
				symbolSize: 5,
				data: rows.map((report) => toNumber(report?.summary?.revenue)),
			},
		],
	};
});

const grossMarginTrendOption = computed(() => {
	const rows = chartReports.value;
	return {
		color: ['#2F6FED', '#27A376'],
		tooltip: { trigger: 'axis' },
		grid: { left: 12, right: 12, top: 36, bottom: 24, containLabel: true },
		xAxis: {
			type: 'category',
			data: chartPeriods.value,
			axisLabel: {
				rotate: chartPeriods.value.length > 8 ? 25 : 0,
				color: 'var(--text-color-secondary, #5e6170)',
				fontSize: 11,
			},
			axisLine: { lineStyle: { color: 'var(--border-color, #dcdcdc)' } },
		},
		yAxis: [
			{
				type: 'value',
				axisLabel: {
					color: 'var(--text-color-secondary, #5e6170)',
					fontSize: 11,
					formatter: (value) => formatCompactCurrency(value),
				},
				splitLine: { lineStyle: { color: 'rgba(0, 0, 0, 0.08)' } },
			},
			{
				type: 'value',
				axisLabel: {
					color: 'var(--text-color-secondary, #5e6170)',
					fontSize: 11,
					formatter: (value) => `${toNumber(value).toFixed(0)}%`,
				},
				splitLine: { show: false },
			},
		],
		series: [
			{
				name: 'Gross margin',
				type: 'bar',
				yAxisIndex: 0,
				barMaxWidth: 30,
				data: rows.map((report) => toNumber(report?.summary?.grossMargin)),
				itemStyle: {
					color: (params) => (toNumber(params.value) < 0 ? '#C45151' : '#2F6FED'),
				},
			},
			{
				name: 'Margin %',
				type: 'line',
				yAxisIndex: 1,
				smooth: true,
				symbolSize: 5,
				data: rows.map((report) => toNumber(report?.summary?.marginPct, 0)),
			},
		],
	};
});

const loadReports = async (page = 1) => {
	try {
		await store.fetchReports({ page });
		currentPage.value = page;
	} catch (err) {
		logError(err, 'AnalyticsListView fetchReports');
	}
};

const loadChartReports = async () => {
	try {
		await store.fetchReportsForCharts();
	} catch (err) {
		logError(err, 'AnalyticsListView fetchReportsForCharts');
	}
};

const changePage = (page) => {
	if (page < 1 || page > pagination.value.pageCount) return;
	loadReports(page);
};

const handleCreate = () => {
	emit('open-editor', { reportId: null });
};

const openReport = (report) => {
	const reportKey = report?.documentId || report?.id;
	if (!reportKey) return;
	emit('open-editor', { reportId: reportKey });
};

const handleDelete = async (report) => {
	if (!report?.documentId && !report?.id) return;
	try {
		await store.deleteReport(report.documentId || report.id);
		// Reload current page after delete
		await loadReports(currentPage.value);
		await loadChartReports();
	} catch (err) {
		logError(err, 'AnalyticsListView deleteReport');
	}
};

const formatDate = (value) => {
	if (!value) return '—';
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return value;
	return date.toLocaleDateString();
};

const formatPeriod = (report) => {
	const from = report?.dateFrom ? format(new Date(report.dateFrom), 'dd MMM yyyy') : '—';
	const to = report?.dateTo ? format(new Date(report.dateTo), 'dd MMM yyyy') : '—';
	return `${from} – ${to}`;
};

const formatCurrency = (value) => {
	if (value === null || value === undefined || value === '') return '—';
	const numeric = Number(value);
	if (!Number.isFinite(numeric)) return '—';
	return new Intl.NumberFormat('en-GB', {
		style: 'currency',
		currency: 'GBP',
	}).format(numeric);
};

const formatPercent = (value) => {
	if (value === null || value === undefined || value === '') return '—';
	const numeric = Number(value);
	if (!Number.isFinite(numeric)) return '—';
	return `${numeric.toFixed(2)}%`;
};

const formatNumber = (value) => {
	if (value === null || value === undefined || value === '') return '—';
	const numeric = Number(value);
	if (!Number.isFinite(numeric)) return '—';
	return numeric.toFixed(2);
};

onMounted(async () => {
	await Promise.all([
		loadReports(pagination.value.page || 1),
		loadChartReports(),
	]);
});

// Reports saved on another device show up here without reloading, in the list and the charts.
const isReportListBusy = () => store.isLoading || store.isChartsLoading;

const refreshReportsQuietly = async ({ canApply }) => {
	if (isReportListBusy()) return;
	const page = currentPage.value;
	const [list, charts] = await Promise.all([store.peekReports({ page }), store.peekReportsForCharts()]);
	if (currentPage.value !== page || !canApply() || isReportListBusy()) return;
	if (!sameRecords(store.reports, list.reports)) {
		store.reports = list.reports;
		store.pagination = list.pagination;
	}
	if (!sameRecords(store.chartReports, charts)) store.chartReports = charts;
};

useLiveRefresh(refreshReportsQuietly, { isBusy: isReportListBusy });

</script>

<style scoped>
/* The list, button and table styles come from the Payments list styles (payment-lists.css). */
.analytics-list {
	display: flex;
	flex-direction: column;
	gap: 16px;
	padding: 24px;
	color: var(--text-color);
}

.analytics-list.is-embedded {
	min-height: 0;
	padding: 0;
}

.list-title {
	margin: 0;
	font: var(--ap-type-title);
	color: var(--text-color);
}

.analytics-error {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 12px;
}

.charts-grid {
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	gap: 16px;
}

.analytics-reports-table {
	min-width: 760px;
}

.analytics-reports-table .actions-col {
	width: 56px;
	padding-block: 0;
	text-align: right;
}

.analytics-reports-table .negative {
	color: var(--noc-red, #c45151);
}

@media (max-width: 1120px) {
	.charts-grid {
		grid-template-columns: 1fr;
	}
}
</style>
